import {
  Briefcase, Plus, ChevronUp, ChevronDown, Trash2, Building, MapPin,
  Navigation, Globe, Phone, Calendar, Clock as ClockIcon, Home, Users,
  Tag, Info, AlignLeft, Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomInput from '../../../../../../../components/forms/CustomInput';
import LocationDropdown from '../../../../../../../components/forms/LocationDropdown';
import CustomSelect from '../../../../../../../components/forms/CustomSelect';
import CustomDatePicker from '../../../../../../../components/forms/CustomDatePicker';
import Clock from '../../../../../../../components/forms/Clock';
import { toast } from 'react-hot-toast';
import vouchersData from '../../../../../../../data/vouchers/vouchersData.json';

const mealPlans = vouchersData.mealPlans || [];

const HotelBookingsSection = ({
  formData,
  setFormData,
  addHotel,
  moveHotel,
  handleArrayFieldChange,
  allLocations
}) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm" id="hotel-bookings-section">
      <div className="flex items-center justify-between gap-2.5 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 bg-teal-50 text-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-inner">
            <Briefcase size={14} className="sm:w-5 sm:h-5" />
          </div>
          <div>
            <h3 className="text-xs sm:text-base font-semibold sm:font-bold text-slate-800 leading-tight">
              Hotel Bookings <span className="text-rose-500 ml-0.5">*</span>
            </h3>
            <p className="hidden sm:block text-xs text-slate-500 font-normal sm:font-medium mt-0.5">
              Manage accommodation details for the trip (at least 1 mandatory)
            </p>
          </div>
        </div>
        <button
          onClick={addHotel}
          className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-4 sm:py-2 bg-primary text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-[11px] sm:text-xs hover:opacity-95 transition-all active:scale-95 shadow-sm shrink-0 cursor-pointer"
          type="button"
        >
          <Plus size={12} className="sm:w-3.5 sm:h-3.5" />
          <span>
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Hotel</span>
          </span>
        </button>
      </div>

      <div className="space-y-6">
        <AnimatePresence mode="popLayout">
          {formData.hotel_bookings?.map((hotel, idx) => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.98, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
              transition={{ type: "spring", stiffness: 350, damping: 25 }}
              key={hotel._id || hotel.id || `hotel-${idx}`}
              id={`hotel-${hotel._id || hotel.id || idx}`}
              className="p-6 bg-white border border-slate-200 rounded-3xl shadow-sm relative group hover:shadow-md hover:border-slate-300 transition-all mb-4"
            >
              <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-black tracking-wider uppercase shrink-0">
                  Hotel #{idx + 1}
                </span>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 mr-1 bg-slate-50 p-1 rounded-xl border border-slate-100">
                    <button
                      onClick={() => moveHotel(idx, 'up')}
                      disabled={idx === 0}
                      className="p-1.5 hover:bg-white rounded-lg text-slate-600 disabled:opacity-30 transition-all shadow-sm disabled:shadow-none cursor-pointer disabled:cursor-not-allowed"
                      type="button"
                      title="Move Up"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      onClick={() => moveHotel(idx, 'down')}
                      disabled={idx === formData.hotel_bookings.length - 1}
                      className="p-1.5 hover:bg-white rounded-lg text-slate-600 disabled:opacity-30 transition-all shadow-sm disabled:shadow-none cursor-pointer disabled:cursor-not-allowed"
                      type="button"
                      title="Move Down"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      if (formData.hotel_bookings.length <= 1) {
                        toast.error('At least 1 hotel booking is mandatory.');
                        return;
                      }
                      setFormData(prev => {
                        const newHotels = prev.hotel_bookings.filter((_, i) => i !== idx);
                        const derivedDests = [];
                        const seenDests = new Set();
                        newHotels.forEach(h => {
                          let hLocId = h.location_id || null;
                          let hCity = (h.city || '').trim();
                          let hLocName = null;
                          if (hLocId && allLocations) {
                            const loc = allLocations.find(l => l._id === hLocId);
                            if (loc) {
                              hLocName = loc.name;
                              if (!hCity) hCity = loc.name;
                            }
                          }
                          if (!hLocId && hCity && allLocations) {
                            const loc = allLocations.find(l => l.name?.toLowerCase().trim() === hCity.toLowerCase());
                            if (loc) {
                              hLocId = loc._id;
                              hLocName = loc.name;
                            }
                          }
                          const dedup = hLocId ? `loc_${hLocId}` : (hCity ? `city_${hCity.toLowerCase()}` : null);
                          if (dedup && !seenDests.has(dedup)) {
                            seenDests.add(dedup);
                            derivedDests.push({
                              city: hCity || hLocName || '',
                              location_id: hLocId || '',
                              location_name: hLocName || hCity || '',
                              journey_date: h.check_in_date || ''
                            });
                          }
                        });
                        return {
                          ...prev,
                          hotel_bookings: newHotels,
                          tour_info: {
                            ...prev.tour_info,
                            destinations: derivedDests.length > 0 ? derivedDests : (prev.tour_info?.destinations || [])
                          }
                        };
                      });
                    }}
                    disabled={formData.hotel_bookings?.length <= 1}
                    className={`flex items-center gap-1.5 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors font-bold text-xs shadow-sm cursor-pointer ${
                      formData.hotel_bookings?.length <= 1 ? 'opacity-40 cursor-not-allowed' : ''
                    }`}
                    title={formData.hotel_bookings?.length <= 1 ? "At least 1 hotel is mandatory" : "Remove Hotel Booking"}
                    type="button"
                  >
                    <Trash2 size={14} /> <span className="hidden sm:inline">Remove</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <div>
                  <CustomInput
                    label="Hotel name *"
                    icon={Building}
                    value={hotel.hotel_name || ''}
                    onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'hotel_name', e.target.value)}
                    placeholder="Hotel Name"
                  />
                </div>

                <div>
                  <LocationDropdown
                    value={hotel.location_id || ''}
                    onChange={(val) => handleArrayFieldChange('hotel_bookings', idx, 'location_id', val)}
                    locations={allLocations}
                    valueKey="_id"
                    label="Assign destination *"
                    placeholder="Select Location"
                    buttonClassName="h-[42px] px-3.5 text-xs font-semibold"
                  />
                </div>

                <div>
                  <CustomInput
                    label="Area / city (manual) *"
                    icon={MapPin}
                    value={hotel.city || ''}
                    onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'city', e.target.value)}
                    readOnly={!!hotel.location_id}
                    placeholder="e.g. South Goa"
                  />
                </div>

                <div>
                  <CustomInput
                    label="Hotel address"
                    icon={Navigation}
                    value={hotel.hotel_address || ''}
                    onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'hotel_address', e.target.value)}
                    placeholder="e.g. Near Calangute Beach, North Goa"
                  />
                </div>

                <div>
                  <CustomInput
                    label="Hotel map link"
                    icon={Globe}
                    type="url"
                    value={hotel.map_link || ''}
                    onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'map_link', e.target.value)}
                    placeholder="https://maps.google.com/..."
                  />
                </div>

                <div>
                  <CustomInput
                    label="Hotel phone number"
                    icon={Phone}
                    value={hotel.hotel_phone || ''}
                    onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'hotel_phone', e.target.value)}
                    placeholder="e.g. +91 98765 43210"
                  />
                </div>

                {/* Row: Check in, Check out, Nights in ONE row */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-1">
                  <div className="col-span-1 sm:col-span-1 lg:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
                      Check in <span className="text-rose-500 ml-0.5">*</span>
                    </label>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <CustomDatePicker
                          value={hotel.check_in_date}
                          onChange={(val) => handleArrayFieldChange('hotel_bookings', idx, 'check_in_date', val)}
                          leftIcon={<Calendar size={14} />}
                          inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl h-[42px] px-3 text-xs font-semibold text-slate-800 transition-all outline-none shadow-sm"
                          className="w-full"
                        />
                      </div>
                      <div className="w-full sm:w-24 shrink-0">
                        <Clock
                          value={hotel.check_in_time || ''}
                          onChange={(val) => handleArrayFieldChange('hotel_bookings', idx, 'check_in_time', val)}
                          placeholder="14:00"
                          inputClassName="h-[42px] px-2 rounded-xl text-xs font-semibold text-slate-800 outline-none shadow-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-span-1 sm:col-span-1 lg:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
                      Check out <span className="text-rose-500 ml-0.5">*</span>
                    </label>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <CustomDatePicker
                          value={hotel.check_out_date}
                          onChange={(val) => handleArrayFieldChange('hotel_bookings', idx, 'check_out_date', val)}
                          leftIcon={<Calendar size={14} />}
                          inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl h-[42px] px-3 text-xs font-semibold text-slate-800 transition-all outline-none shadow-sm"
                          className="w-full"
                        />
                      </div>
                      <div className="w-full sm:w-24 shrink-0">
                        <Clock
                          value={hotel.check_out_time || ''}
                          onChange={(val) => handleArrayFieldChange('hotel_bookings', idx, 'check_out_time', val)}
                          placeholder="11:00"
                          inputClassName="h-[42px] px-2 rounded-xl text-xs font-semibold text-slate-800 outline-none shadow-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-span-1 sm:col-span-2 lg:col-span-1">
                    <CustomInput
                      label="Nights"
                      icon={ClockIcon}
                      type="number"
                      min="0"
                      value={hotel.no_of_nights || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || Number(val) >= 0) {
                          handleArrayFieldChange('hotel_bookings', idx, 'no_of_nights', val);
                        }
                      }}
                      placeholder="Nights"
                    />
                  </div>
                </div>

                {/* Row: Rooms, Total pax, Room category, Confirmation in ONE row */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
                  <div>
                    <CustomInput
                      label="Rooms *"
                      icon={Home}
                      type="number"
                      min="0"
                      value={hotel.no_of_rooms || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || Number(val) >= 0) {
                          handleArrayFieldChange('hotel_bookings', idx, 'no_of_rooms', val);
                        }
                      }}
                      placeholder="Rooms"
                    />
                  </div>

                  <div>
                    <CustomInput
                      label="Total pax"
                      icon={Users}
                      type="number"
                      min="0"
                      value={hotel.total_guests || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || Number(val) >= 0) {
                          handleArrayFieldChange('hotel_bookings', idx, 'total_guests', val);
                        }
                      }}
                      placeholder="Pax"
                    />
                  </div>

                  <div>
                    <CustomInput
                      label="Room category"
                      icon={Tag}
                      value={hotel.room_category || ''}
                      onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'room_category', e.target.value)}
                      placeholder="e.g. Deluxe Room"
                    />
                  </div>

                  <div>
                    <CustomInput
                      label="Confirmation number"
                      icon={Info}
                      mono
                      value={hotel.confirmation_number || ''}
                      onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'confirmation_number', e.target.value)}
                      placeholder="e.g. CN-98214"
                    />
                  </div>
                </div>

                {/* Row: Meal plan & Inclusions */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 pt-1">
                  <div>
                    <CustomSelect
                      label="Meal plan *"
                      value={hotel.meal_plan || ''}
                      onChange={(val) => handleArrayFieldChange('hotel_bookings', idx, 'meal_plan', val)}
                      options={mealPlans}
                      icon={AlignLeft}
                      placeholder="Select"
                      buttonClassName="h-[42px] px-3.5 text-xs font-semibold"
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                    <CustomInput
                      label="Hotel inclusions & amenities"
                      icon={Sparkles}
                      value={hotel.inclusions || ''}
                      onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'inclusions', e.target.value)}
                      placeholder="e.g. Welcome Drink, Breakfast, Airport Transfer, 20% Spa Discount"
                    />
                  </div>
                </div>

                {/* Custom Fields Section */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3 border-t border-slate-100 pt-4 mt-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-bold text-slate-700 shrink-0">
                      Custom Details
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const currentFields = hotel.custom_fields || [];
                        handleArrayFieldChange('hotel_bookings', idx, 'custom_fields', [
                          ...currentFields,
                          { label: '', value: '' }
                        ]);
                      }}
                      className="text-xs font-bold text-primary hover:underline flex items-center gap-1 shrink-0 cursor-pointer"
                    >
                      <Plus size={13} /> Add Custom Field
                    </button>
                  </div>
                  <div className="space-y-3">
                    {hotel.custom_fields && hotel.custom_fields.length > 0 ? (
                      hotel.custom_fields.map((field, cfIdx) => (
                        <div key={field.id || cfIdx} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                          <CustomInput
                            value={field.label !== undefined ? field.label : (field.key || '')}
                            onChange={(e) => {
                              const updatedFields = [...(hotel.custom_fields || [])];
                              updatedFields[cfIdx] = { ...updatedFields[cfIdx], label: e.target.value, key: e.target.value };
                              handleArrayFieldChange('hotel_bookings', idx, 'custom_fields', updatedFields);
                            }}
                            placeholder="Field label (e.g. Bed Type)"
                            className="w-full sm:w-1/3 min-w-0"
                            inputClassName="bg-slate-50 focus:bg-white h-[38px] text-xs font-semibold px-3"
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <CustomInput
                              value={field.value || ''}
                              onChange={(e) => {
                                const updatedFields = [...(hotel.custom_fields || [])];
                                updatedFields[cfIdx] = { ...updatedFields[cfIdx], value: e.target.value };
                                handleArrayFieldChange('hotel_bookings', idx, 'custom_fields', updatedFields);
                              }}
                              placeholder="Field value (e.g. King Size)"
                              className="flex-1 min-w-0"
                              inputClassName="bg-slate-50 focus:bg-white h-[38px] text-xs font-semibold px-3"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updatedFields = (hotel.custom_fields || []).filter((_, fi) => fi !== cfIdx);
                                handleArrayFieldChange('hotel_bookings', idx, 'custom_fields', updatedFields);
                              }}
                              className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors shrink-0 cursor-pointer"
                              title="Delete Field"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">No custom fields added yet.</p>
                    )}
                  </div>
                </div>

                {/* Special Requests */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <CustomInput
                    label="Special requests / notes"
                    icon={Tag}
                    value={hotel.special_requests || ''}
                    onChange={(e) => handleArrayFieldChange('hotel_bookings', idx, 'special_requests', e.target.value)}
                    placeholder="e.g. Non-smoking room, early check-in requested"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HotelBookingsSection;
